const LOCAL_API_URL = "http://localhost:8787";
const STAGING_API_URL = "https://staging-api.hashee.ai";
const PRODUCTION_API_URL = "https://api.hashee.ai";

function resolveApiBaseUrl(): string {
  const configuredUrl = (import.meta.env.VITE_API_URL ?? "").trim();
  if (configuredUrl.length > 0) {
    return configuredUrl.replace(/\/+$/, "");
  }

  const hostname = window.location.hostname;
  if (
    hostname === "staging-admin.hashee.ai" ||
    hostname === "hashee-admin-staging.pages.dev" ||
    hostname.endsWith(".hashee-admin-staging.pages.dev")
  ) {
    return STAGING_API_URL;
  }

  if (
    hostname === "admin.hashee.ai" ||
    hostname === "hashee-admin.pages.dev" ||
    hostname.endsWith(".hashee-admin.pages.dev")
  ) {
    return PRODUCTION_API_URL;
  }

  return LOCAL_API_URL;
}

const BASE_URL = resolveApiBaseUrl();

const TOKEN_KEY = "hashee_admin_console_token";
const REFRESH_TOKEN_KEY = "hashee_admin_console_refresh_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  clearRefreshToken();
}

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    i18n_key?: string;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly errorCode: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    // response is not JSON
  }
  const code = body.error?.code ?? `HTTP_${res.status}`;
  const msg = body.error?.message ?? res.statusText;
  return new ApiError(res.status, code, msg);
}

interface RefreshResponseBody {
  data?: {
    access_token?: unknown;
    refresh_token?: unknown;
  };
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  const body = refreshToken
    ? JSON.stringify({ refresh_token: refreshToken })
    : undefined;
  const headers = new Headers();
  if (body) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`${BASE_URL}/admin-auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });

  if (!res.ok) {
    clearToken();
    return false;
  }

  const parsed = (await res.json()) as RefreshResponseBody;
  const accessToken = parsed.data?.access_token;
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    clearToken();
    return false;
  }

  setToken(accessToken);
  const nextRefreshToken = parsed.data?.refresh_token;
  if (typeof nextRefreshToken === "string" && nextRefreshToken.length > 0) {
    setRefreshToken(nextRefreshToken);
  }
  return true;
}

function shouldTryRefresh(path: string): boolean {
  return path !== "/admin-auth/login" && path !== "/admin-auth/refresh";
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retryOnUnauthorized = true,
): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }

  const fullUrl = `${BASE_URL}${path}`;
  const res = await fetch(fullUrl, {
    ...options,
    credentials: options.credentials ?? "include",
    headers,
  });

  if (res.status === 401) {
    if (retryOnUnauthorized && shouldTryRefresh(path)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return await apiFetch<T>(path, options, false);
      }
    }

    clearToken();
    if (shouldTryRefresh(path)) {
      window.location.href = "/login";
    }
    throw await parseErrorBody(res);
  }

  if (!res.ok) {
    throw await parseErrorBody(res);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}
